import { useMemo } from 'react';
import { $, fH, MarginChip } from './utils';

export default function ClientsTab({ clients, users, excludeOps, directCost }) {
  const EXCLUDED_NAMES = new Set(['Diego Prusky','Bruni Salmeron','Chuny Coletti','Ro Zicarelli']);
  const EXCLUDED_POSITIONS = new Set(['New Business']);

  const processed = useMemo(() => {
    return clients.map(c => {
      let team = Array.isArray(c.team_json) ? c.team_json : (c.team_json ? JSON.parse(c.team_json) : []);

      if (excludeOps) {
        team = team.filter(t => !EXCLUDED_NAMES.has(t.name) && !EXCLUDED_POSITIONS.has(t.position));
      }

      if (directCost) {
        // Direct cost: hours × hourly_rate per person
        const rateMap = {};
        for (const u of users) rateMap[u.user_name] = u.hourly_rate || 0;
        team = team.map(t => {
          const directC = (t.hours || 0) * (rateMap[t.name] || 0);
          return { ...t, cost_attributed: directC, profit_attributed: (t.revenue_attributed||0) - directC };
        });
      }

      const rev    = c.revenue;
      const cost   = team.reduce((s,t)=>s+(t.cost_attributed||0), 0);
      const profit = rev - cost;
      const margin = rev > 0 ? profit / rev * 100 : null;
      return { ...c, _team: team, _cost: cost, _profit: profit, _margin: margin };
    }).filter(c => c.revenue > 0 || c._cost > 0)
      .sort((a,b) => b.revenue - a.revenue);
    }, [clients, users, excludeOps, directCost]);

  const maxRev   = Math.max(...processed.map(c=>c.revenue), 1);
  const maxAbsP  = Math.max(...processed.map(c=>Math.abs(c._profit)), 1);
  const profSort = [...processed].sort((a,b)=>b._profit-a._profit);

  return (
    <>
      {/* Bar charts */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
        <BarCard title="Revenue by Client">
          {processed.map(c=>(
            <BarRow key={c.client_name} label={c.client_name}
              value={$(c.revenue)} width={c.revenue/maxRev*100} color="var(--blue)" />
          ))}
        </BarCard>
        <BarCard title="Profit by Client">
          {profSort.map(c=>(
            <BarRow key={c.client_name} label={c.client_name}
              value={$(c._profit)} width={Math.abs(c._profit)/maxAbsP*100}
              color={c._profit>=0?'var(--green)':'var(--red)'}
              valueColor={c._profit>=0?'var(--green)':'var(--red)'} />
          ))}
        </BarCard>
      </div>

      {/* Client cards grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(420px,1fr))', gap:16 }}>
        {processed.map(c => {
          const topTeam = [...c._team].sort((a,b)=>(b.hours||0)-(a.hours||0)).slice(0,8);
          return (
            <div key={c.client_name} style={{
              background:'var(--card)', border:'1px solid var(--border)',
              borderRadius:14, padding:20,
              transition:'.15s',
            }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--green)'; e.currentTarget.style.boxShadow='0 2px 8px rgba(22,163,74,.08)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='none';}}
            >
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <h3 style={{ fontSize:16, fontWeight:700 }}>{c.client_name}</h3>
                <MarginChip value={c._margin} />
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:12 }}>
                {[
                  { label:'Revenue', value:$(c.revenue), color:'var(--blue)' },
                  { label:'Cost',    value:$(c._cost),   color:'var(--text)' },
                  { label:'Profit',  value:$(c._profit), color:c._profit>=0?'var(--green)':'var(--red)' },
                ].map(m=>(
                  <div key={m.label} style={{ textAlign:'center' }}>
                    <div style={{ fontFamily:'DM Mono,monospace', fontSize:15, fontWeight:700, color:m.color }}>{m.value}</div>
                    <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.5px' }}>{m.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:10 }}>
                {fH(c.billable_hours)}h billable · {c._team.length} people
              </div>

              <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.8 }}>
                {topTeam.map(t=>(
                  <div key={t.name} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                    <span style={{ fontWeight:500, color:'var(--text)' }}>{t.name}</span>
                    <span style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--text3)' }}>
                      {fH(t.hours||0)}h
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function BarCard({ title, children }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
      <h4 style={{ fontSize:13, fontWeight:700, marginBottom:12, color:'var(--text2)' }}>{title}</h4>
      {children}
    </div>
  );
}

function BarRow({ label, value, width, color, valueColor }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, fontSize:12 }}>
      <div style={{ width:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:500 }}>{label}</div>
      <div style={{ flex:1, height:8, background:'var(--card2)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:`${width}%`, height:'100%', borderRadius:4, background:color }} />
      </div>
      <div style={{ width:80, textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:11, color:valueColor||'var(--text2)' }}>{value}</div>
    </div>
  );
}
